import { Component, inject, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormArray,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { RestaurantService } from '../../../../services/restaurant.service';
import { FNFResponse } from '../../../../models/response';
import { ImageCropingComponent } from '../../../modals/image-croping/image-croping.component';
import {
  croppedImageFile,
  sourceImageError,
  uploadSizeError,
} from '../../../../utils/image-export.util';

const DEFAULT_COVER_IMAGE_URL =
  'https://forknfind-public-dev.s3.us-east-1.amazonaws.com/public/restaurent_default_cover.png';
const DEFAULT_LOGO_URL =
  'https://forknfind-public-dev.s3.us-east-1.amazonaws.com/public/restaurent_default_logo.png';

import { formatNational, parsePhone } from '../../../../utils/phone-number.util';
import { DateException, groupsOfType } from '../../../../utils/serving-hours.util';
import { canEditHours, getLoggedInUserRole } from '../../../../utils/user-role.util';
import { DateExceptionsComponent } from '../../../controls/date-exceptions/date-exceptions.component';
import { isPosManaged } from '../../../../utils/pos-provider.util';
import { RestaurantStatusComponent } from '../restaurant-status/restaurant-status.component';

// Restaurant hours (FNF-1237). A close time earlier than the open time means the
// slot runs past midnight and closes the next day.
const MIN_SLOT_MINUTES = 30;
type TimeRangeError = 'same' | 'tooShort';
@Component({
  selector: 'app-restaurant-profile',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatIcon,
    CommonModule,
    RestaurantStatusComponent,
    DateExceptionsComponent,
  ],
  templateUrl: './restaurant-profile.component.html',
  styleUrl: './restaurant-profile.component.css',
})
export class RestaurantProfileComponent {
  imagePreview: string | null = null;
  restaurantService = inject(RestaurantService);
  ngxSpinnerService = inject(NgxSpinnerService);
  private cdr = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  /**
   * Holidays & closed dates (FNF-1612): the list Reservation settings shows
   * too. Only for the roles the API lets change them.
   */
  readonly canEditClosedDates = canEditHours(getLoggedInUserRole());
  dateExceptions: DateException[] = [];
  closedDatesState: 'loading' | 'ready' | 'failed' = 'loading';
  /** `?section=status` (the header's closure chip) scrolls there once loaded. */
  private pendingSection: string | null = null;
  currentRestaurant: any;
  isNotFnfHardware = false;
  profileForm: FormGroup;
  imageSizeError: any;
  displayLoading: boolean = false;
  private selectedFile: File | null = null;
  private selectedCoverFile: File | null = null;
  selectedProfile: string = 'profile';
  hoursForm: FormGroup;
  daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  dayNameMap: { [key: string]: string } = {
    Mon: 'Mon',
    Tue: 'Tue',
    Wed: 'Wed',
    Thu: 'Thu',
    Fri: 'Fri',
    Sat: 'Sat',
    Sun: 'Sun',
  };
  timeOptions: string[] = [];
  timeOptionsDisplay: { value: string; display: string }[] = [];
  servingHours: any = [];
  restaurantServingHoursId: any;
  coverImagePreview: string | null = null;
  coverImageSizeError: any;
  private initialHoursFormSnapshot = '';

  // Check if serving hours exist (for button text)
  hasServingHours(): boolean {
    return this.servingHours && Array.isArray(this.servingHours) && this.servingHours.length > 0;
  }

  constructor(
    private dialog: MatDialog,
    private toastr: ToastrService,
    private fb: FormBuilder,
    private router: Router,
  ) {
    // Generate time options with 30-minute intervals in 24-hour format
    this.timeOptions = this.generateTimeOptions();
    // Generate time options with display text in 12-hour format
    this.timeOptionsDisplay = this.generateTimeOptionsWithDisplay();

    this.profileForm = this.fb.group({
      restaurant_name: ['', [Validators.required, Validators.minLength(3)]],
      address: ['', [Validators.required, Validators.minLength(3)]],
      email: [
        '',
        [Validators.required, Validators.pattern(/^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/)],
      ],
      // display_name: ['',[Validators.required, Validators.minLength(3)]],
      phone_number: [''],
      logo_url: [''],
      image_url: [''],
      time_zone: [''],
    });
    this.profileForm.get('address')?.disable();
    this.profileForm.get('email')?.disable();
    this.profileForm.get('phone_number')?.disable();
    this.profileForm.get('time_zone')?.disable();

    // Initialize hours form
    this.hoursForm = this.fb.group(
      { schedules: this.fb.array([]) },
      { validators: () => this.crossDayOverlapValidator() },
    );

    // Add default schedule
    this.addSchedule();
    this.captureHoursFormSnapshot();
  }

  hasHoursFormChanges(): boolean {
    return JSON.stringify(this.buildHoursFormPayload()) !== this.initialHoursFormSnapshot;
  }

  private captureHoursFormSnapshot(): void {
    this.initialHoursFormSnapshot = JSON.stringify(this.buildHoursFormPayload());
  }

  private buildHoursFormPayload(): {
    serving_hours: {
      days_of_the_week: string[];
      hours: { start_time: string; end_time: string }[];
    }[];
  } {
    const servingHours: {
      days_of_the_week: string[];
      hours: { start_time: string; end_time: string }[];
    }[] = [];

    for (let i = 0; i < this.schedules.length; i++) {
      const schedule = this.getSchedule(i);
      const daysGroup = schedule.get('days') as FormGroup;
      const timeSlotsArray = this.getTimeSlots(i);

      const selectedDays: string[] = [];
      this.daysOfWeek.forEach((day) => {
        if (daysGroup.get(day)?.value === true) {
          selectedDays.push(this.dayNameMap[day]);
        }
      });

      if (selectedDays.length === 0) {
        continue;
      }

      const hours: { start_time: string; end_time: string }[] = [];
      for (let j = 0; j < timeSlotsArray.length; j++) {
        const timeSlot = this.getTimeSlot(i, j);
        const openTime = timeSlot.get('openTime')?.value || '';
        const closeTime = timeSlot.get('closeTime')?.value || '';

        if (
          openTime &&
          closeTime &&
          String(openTime).trim() !== '' &&
          String(closeTime).trim() !== ''
        ) {
          hours.push({
            start_time: String(openTime).trim(),
            end_time: String(closeTime).trim(),
          });
        }
      }

      servingHours.push({
        days_of_the_week: selectedDays.sort(),
        hours,
      });
    }

    servingHours.sort((a, b) =>
      a.days_of_the_week.join(',').localeCompare(b.days_of_the_week.join(',')),
    );

    return { serving_hours: servingHours };
  }

  ngAfterViewInit() {
    this.getRestaurantServingHours();
    document.querySelector('.home-body')?.addEventListener('scroll', this.onPageScroll);
  }

  // Generate time options with 15-minute intervals in 24-hour format, ending at 23:59
  generateTimeOptions(): string[] {
    const options: string[] = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        options.push(timeString);
      }
    }
    options.push('23:59');
    return options;
  }

  // Generate time options with display text in 12-hour format with AM/PM
  generateTimeOptionsWithDisplay(): { value: string; display: string }[] {
    const options: { value: string; display: string }[] = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const display = this.convertTo12HourFormat(time24);
        options.push({ value: time24, display: display });
      }
    }
    // Last option of the day: 11:59 PM
    options.push({ value: '23:59', display: this.convertTo12HourFormat('23:59') });
    return options;
  }

  /** Close before open: the slot runs past midnight and closes the next day. */
  isOvernight(openTime: string, closeTime: string): boolean {
    return this.timeToMinutes(closeTime) < this.timeToMinutes(openTime);
  }

  /** Why open → close is not a valid slot, or null when it is. */
  getTimeRangeError(openTime: string, closeTime: string): TimeRangeError | null {
    const open = this.timeToMinutes(openTime);
    const close = this.timeToMinutes(closeTime);
    if (close === open) {
      return 'same';
    }
    const length = close > open ? close - open : close + 24 * 60 - open;
    return length < MIN_SLOT_MINUTES ? 'tooShort' : null;
  }

  /** Whether the slot's close time falls on the next day (for the "Next day" label). */
  isNextDayClose(scheduleIndex: number, timeSlotIndex: number): boolean {
    const timeSlot = this.getTimeSlot(scheduleIndex, timeSlotIndex);
    const openTime = timeSlot?.get('openTime')?.value;
    const closeTime = timeSlot?.get('closeTime')?.value;
    return !!openTime && !!closeTime && this.isOvernight(openTime, closeTime);
  }

  // Convert 24-hour format to 12-hour format with AM/PM
  convertTo12HourFormat(time24: string): string {
    const [hours, minutes] = time24.split(':');
    const hour24 = parseInt(hours, 10);
    const min = minutes;

    let hour12 = hour24;
    const period = hour24 >= 12 ? 'PM' : 'AM';

    if (hour12 === 0) {
      hour12 = 12;
    } else if (hour12 > 12) {
      hour12 -= 12;
    }

    return `${hour12.toString().padStart(2, '0')}:${min} ${period}`;
  }

  ngOnInit() {
    // First, so displayLoading is set before a ?section= asks to scroll.
    this.getCurrentRestaurantDetails();
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.pendingSection = params.get('section');
      this.scrollToPendingSection();
    });
  }

  private scrollToPendingSection(attempt = 0) {
    if (!this.pendingSection || this.displayLoading) {
      return;
    }
    const section = this.pendingSection;
    // The sections render a moment after the details load; scrolling before
    // the target is there only moved the rail (found end to end, FNF-1612).
    if (!document.getElementById('section-' + section)) {
      if (attempt < 20) {
        setTimeout(() => this.scrollToPendingSection(attempt + 1), 100);
      }
      return;
    }
    this.pendingSection = null;
    this.scrollToSection(section);
  }

  formatPhoneNumber(stored: string): string {
    const phone = parsePhone(stored, 'US');
    return formatNational(phone.country, phone.national);
  }

  getCurrentRestaurantDetails() {
    this.displayLoading = true;
    this.restaurantService.getRestaurantDetails().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.displayLoading = false;
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.displayLoading = false;
          return;
        }
        this.currentRestaurant = response.data;
        if (isPosManaged(this.currentRestaurant?.pos_provider)) {
          this.isNotFnfHardware = true;
        }
        this.displayLoading = false;
        this.setProfileForm(this.currentRestaurant);
        this.scrollToPendingSection();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.displayLoading = false;
      },
    });
  }

  setProfileForm(data: any) {
    this.profileForm.patchValue({
      restaurant_name: data.restaurant_name,
      address: data.address?.formatted_address,
      email: data.email || '',
      // Not E.164 yet: read as US, shown "(806) 999-5417".
      phone_number: this.formatPhoneNumber(data.phone_number || ''),
      logo_url: data.logo_url,
      image_url: data.image_url || '',
      time_zone: data.time_zone || '',
    });
    this.imagePreview = data.logo_url;
    this.coverImagePreview = data.image_url;
    this.validateForm();
  }

  validateForm(): void {
    Object.keys(this.profileForm.controls).forEach((field) => {
      const control = this.profileForm.get(field);
      if (control) {
        control.markAsTouched();
        control.updateValueAndValidity();
      }
    });
  }

  changeProfileDetails(restaurant: any) {
    this.ngxSpinnerService.show();
    this.restaurantService
      .editRestaurantDetails(restaurant, this.currentRestaurant.restaurant_id)
      .subscribe({
        next: (response: FNFResponse<any>) => {
          if (!response.data && response.error) {
            this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          } else if (!response.data) {
            this.toastr.error('No data found!', 'Error');
          } else {
            this.toastr.success(response.data.message, 'Success');
            this.currentRestaurant = { ...this.currentRestaurant, ...restaurant };
            this.imagePreview = restaurant.logo_url;
            this.coverImagePreview = restaurant.image_url;
            this.restaurantService.updateRestaurantLogo$.next(restaurant);
          }
          this.ngxSpinnerService.hide();
        },
        error: () => {
          this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
          this.ngxSpinnerService.hide();
        },
      });
  }

  resetForm() {
    this.setProfileForm(this.currentRestaurant);
  }

  get f() {
    return this.profileForm.controls;
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      // The 1MB upload limit is checked after crop + compression
      const sourceError = sourceImageError(file);
      if (sourceError) {
        this.imageSizeError = sourceError;
        return;
      }
      this.imageSizeError = null;
      this.selectedFile = file;

      // Open crop modal
      const reader = new FileReader();
      reader.onload = () => {
        const imageSrc = reader.result as string;
        this.openCropModal(imageSrc);
      };
      reader.readAsDataURL(file);
    }
  }

  onCoverFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      // The 1MB upload limit is checked after crop + compression
      const sourceError = sourceImageError(file);
      if (sourceError) {
        this.coverImageSizeError = sourceError;
        return;
      }
      this.coverImageSizeError = null;
      this.selectedCoverFile = file || null;
      // Open crop modal
      const reader = new FileReader();
      reader.onload = () => {
        const imageSrc = reader.result as string;
        this.openCoverCropModal(imageSrc);
      };
      reader.readAsDataURL(file);
    }
  }

  openCropModal(imageSrc: string): void {
    const dialogRef = this.dialog.open(ImageCropingComponent, {
      width: '700px',
      maxWidth: '90vw',
      disableClose: true,
      data: {
        imageSrc: imageSrc,
        // Logos are always square.
        targetWidth: 300,
        targetHeight: 300,
        maxLongEdge: 1024,
        // Logos may have a transparent background: WebP, or PNG where WebP can't be encoded
        allowTransparency: true,
        title: 'Crop Restaurant Logo',
        isImageFrom: 'restaurant-profile',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      const uploadError = result?.blob ? uploadSizeError(result.blob) : null;
      if (result && result.croppedImage && !uploadError) {
        this.imagePreview = result.croppedImage;
        const file = croppedImageFile(result.blob, 'cropped-logo');
        this.selectedFile = file;
        this.setLogoImageFormData(file, 'logo');
      } else {
        if (uploadError) {
          this.imageSizeError = uploadError;
        }
        // User cancelled or the result is too large, reset file input
        const fileInput = document.getElementById('fileInput') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
        this.selectedFile = null;
      }
    });
  }

  openCoverCropModal(imageSrc: string): void {
    const dialogRef = this.dialog.open(ImageCropingComponent, {
      width: '700px',
      maxWidth: '90vw',
      disableClose: true,
      data: {
        imageSrc: imageSrc,
        targetWidth: 278,
        targetHeight: 112,
        defaultAspectRatio: 'original',
        title: 'Crop Restaurant Cover Image',
        isImageFrom: 'restaurant-cover',
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      const uploadError = result?.blob ? uploadSizeError(result.blob) : null;
      if (result && result.croppedImage && !uploadError) {
        this.coverImagePreview = result.croppedImage;
        const file = croppedImageFile(result.blob, 'cropped-cover');
        this.selectedCoverFile = file;
        this.setCoverImageFormData(file, 'cover');
      } else {
        if (uploadError) {
          this.coverImageSizeError = uploadError;
        }
        // User cancelled or the result is too large, reset file input so the same file can be picked again
        const coverFileInput = document.getElementById('coverFileInput') as HTMLInputElement;
        if (coverFileInput) {
          coverFileInput.value = '';
        }
        this.selectedCoverFile = null;
      }
    });
  }

  removeProfile() {
    this.imagePreview = null;
    this.imageSizeError = null;
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  removeCoverImage() {
    this.coverImagePreview = null;
    this.coverImageSizeError = null;
    const coverFileInput = document.getElementById('coverFileInput') as HTMLInputElement;
    if (coverFileInput) {
      coverFileInput.value = '';
    }
  }

  // User-initiated removal: clear the preview and fall back to the default image on save.
  resetLogoToDefault() {
    this.removeProfile();
    this.profileForm.get('logo_url')?.setValue(DEFAULT_LOGO_URL);
  }

  resetCoverToDefault() {
    this.removeCoverImage();
    this.profileForm.get('image_url')?.setValue(DEFAULT_COVER_IMAGE_URL);
  }

  onSubmit() {
    this.changeProfileDetails(this.buildProfilePayload());
  }

  hasProfileFormChanges(): boolean {
    if (!this.currentRestaurant) {
      return false;
    }
    const payload = this.buildProfilePayload();
    return (
      payload.image_url !== (this.currentRestaurant.image_url || '') ||
      payload.logo_url !== this.currentRestaurant.logo_url ||
      payload.restaurant_name !== this.currentRestaurant.restaurant_name
    );
  }

  private buildProfilePayload() {
    return {
      image_url: this.profileForm.get('image_url')?.value,
      logo_url: this.profileForm.get('logo_url')?.value,
      restaurant_name: this.profileForm.get('restaurant_name')?.value,
    };
  }

  setLogoImageFormData(file: File, fileName: string) {
    let formData = new FormData();
    formData.append('file_id', fileName);
    formData.append('file_type', 'profiles');
    formData.append('is_public', 'true');
    formData.append('file', file);
    this.saveLogoImage(formData, 'logo');
  }

  setCoverImageFormData(file: File, fileName: string) {
    let formData = new FormData();
    formData.append('file_id', fileName);
    formData.append('file_type', 'profiles');
    formData.append('is_public', 'true');
    formData.append('file', file);
    this.saveLogoImage(formData, 'cover');
  }

  saveLogoImage(formData: any, type: string) {
    this.ngxSpinnerService.show();
    this.restaurantService.addImage(formData).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          this.removeProfile();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          this.removeProfile();
          return;
        }
        if (type === 'logo') {
          this.profileForm.get('logo_url')?.setValue(response.data.url);
        } else if (type === 'cover') {
          this.profileForm.get('image_url')?.setValue(response.data.url);
        }
        this.ngxSpinnerService.hide();
      },
      error: (error) => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
        this.removeProfile();
      },
    });
  }

  onImageError(event: Event): void {
    (event.target as HTMLImageElement).src = 'assets/default/logo.png';
  }

  onCoverImageError(event: Event): void {
    (event.target as HTMLImageElement).src = 'assets/default/cover.png';
  }

  filterProfile(type: string) {
    this.selectedProfile = type;
  }

  /**
   * The sections live on one scrolling page. The app scrolls `.home-body`
   * rather than the window, so scroll that container explicitly.
   */
  scrollToSection(type: string) {
    this.selectedProfile = type;
    const container = document.querySelector('.home-body') as HTMLElement | null;
    const target = document.getElementById('section-' + type);
    if (!container || !target) {
      return;
    }
    const offset = target.getBoundingClientRect().top - container.getBoundingClientRect().top;
    // Assigning scrollTop rather than scrollTo({behavior:'smooth'}): smooth
    // scrolling on this container is unreliable and can stall a few pixels in.
    container.scrollTop = container.scrollTop + offset - 8;
  }

  /** Keeps the rail in sync with whichever section the user has scrolled to. */
  private onPageScroll = () => {
    const container = document.querySelector('.home-body') as HTMLElement | null;
    if (!container) {
      return;
    }
    // The last section whose top is in the upper half of the view.
    const containerTop = container.getBoundingClientRect().top;
    this.selectedProfile = 'profile';
    for (const type of ['status', 'hours', 'closed-dates']) {
      const section = document.getElementById('section-' + type);
      if (
        section &&
        section.getBoundingClientRect().top - containerTop <= container.clientHeight / 2
      ) {
        this.selectedProfile = type;
      }
    }
  };

  // Get schedules FormArray
  get schedules(): FormArray {
    return this.hoursForm.get('schedules') as FormArray;
  }

  // Get schedule FormGroup at index
  getSchedule(index: number): FormGroup {
    return this.schedules.at(index) as FormGroup;
  }

  // Get time slots FormArray for a specific schedule
  getTimeSlots(scheduleIndex: number): FormArray {
    return this.schedules.at(scheduleIndex).get('timeSlots') as FormArray;
  }

  // Get time slot FormGroup at index
  getTimeSlot(scheduleIndex: number, timeSlotIndex: number): FormGroup {
    return this.getTimeSlots(scheduleIndex).at(timeSlotIndex) as FormGroup;
  }

  /**
   * Every time is offered once an open time is set. A close earlier than the
   * open closes the next day; timeRangeValidator rejects what isn't valid.
   */
  getCloseTimeOptionsDisplay(
    scheduleIndex: number,
    timeSlotIndex: number,
  ): { value: string; display: string }[] {
    const openTime = this.getTimeSlot(scheduleIndex, timeSlotIndex).get('openTime')?.value;
    return openTime ? this.timeOptionsDisplay : [];
  }

  canSelectCloseTime(scheduleIndex: number, timeSlotIndex: number): boolean {
    return !!this.getTimeSlot(scheduleIndex, timeSlotIndex).get('openTime')?.value;
  }

  // Get selected time for display
  getSelectedTime(
    scheduleIndex: number,
    timeSlotIndex: number,
    timeType: 'open' | 'close',
  ): string {
    const timeSlot = this.getTimeSlot(scheduleIndex, timeSlotIndex);
    const time = timeSlot.get(timeType === 'open' ? 'openTime' : 'closeTime')?.value;
    if (time) {
      return this.convertTo12HourFormat(time);
    }
    return 'Select time';
  }

  // Change time selection
  changeTime(
    scheduleIndex: number,
    timeSlotIndex: number,
    timeType: 'open' | 'close',
    time: string,
  ): void {
    const timeSlot = this.getTimeSlot(scheduleIndex, timeSlotIndex);
    const controlName = timeType === 'open' ? 'openTime' : 'closeTime';
    const control = timeSlot.get(controlName);

    if (control) {
      control.setValue(time);
      control.markAsTouched();
      control.markAsDirty();
    }

    const otherControlName = timeType === 'open' ? 'closeTime' : 'openTime';
    const otherControl = timeSlot.get(otherControlName);

    // The close time is kept when the open time changes: every close time is
    // offered, and timeRangeValidator shows why a pair is invalid instead.

    // Only touch the paired field when open time changes (not when editing close time)
    if (timeType === 'open' && otherControl?.value) {
      otherControl.markAsTouched();
      otherControl.markAsDirty();
    }

    timeSlot.markAsTouched();
    timeSlot.markAsDirty();

    // Trigger validation on the entire time slot group to check time range
    timeSlot.updateValueAndValidity();

    // Validate for duplicate time slots and sequence order after form updates are complete
    // Use setTimeout to ensure Angular has processed the value change
    setTimeout(() => {
      this.validateDuplicateTimeSlots(scheduleIndex);
      if (timeType === 'open') {
        this.validateTimeSlotsSequence(scheduleIndex);
      } else {
        // Previous slot close time affects this and following slots' open times
        const timeSlots = this.getTimeSlots(scheduleIndex);
        for (let i = Math.max(1, timeSlotIndex); i < timeSlots.length; i++) {
          this.validateTimeSlotSequenceAt(scheduleIndex, i);
        }
      }
      this.cdr.detectChanges();
    }, 50);
  }

  // Check if time slot has validation errors
  hasTimeSlotError(
    scheduleIndex: number,
    timeSlotIndex: number,
    errorType: string,
    field?: 'open' | 'close',
  ): boolean {
    const timeSlot = this.getTimeSlot(scheduleIndex, timeSlotIndex);
    if (!timeSlot) return false;

    // A cross-day overlap is about the open time, so it borders the open field
    // the same way a sequence error does.
    const crossDayOnOpen =
      errorType === 'sequenceInvalid' &&
      field !== 'close' &&
      !!this.getCrossDayOverlap(scheduleIndex, timeSlotIndex);
    const hasError = timeSlot.hasError(errorType) === true || crossDayOnOpen;
    if (!hasError) return false;

    const openTimeControl = timeSlot.get('openTime');
    const closeTimeControl = timeSlot.get('closeTime');

    // Sequence error applies to open time field only
    if (errorType === 'sequenceInvalid') {
      if (field === 'close') {
        return false;
      }
      return hasError;
    }

    const groupTouched = timeSlot.dirty || timeSlot.touched;
    const controlsTouched = !!(
      openTimeControl?.dirty ||
      openTimeControl?.touched ||
      closeTimeControl?.dirty ||
      closeTimeControl?.touched
    );

    return !!(groupTouched || controlsTouched);
  }

  // Check if time slot error should be displayed
  shouldDisplayTimeSlotError(scheduleIndex: number, timeSlotIndex: number): boolean {
    const timeSlot = this.getTimeSlot(scheduleIndex, timeSlotIndex);
    if (!timeSlot) return false;

    const openTimeControl = timeSlot.get('openTime');
    const closeTimeControl = timeSlot.get('closeTime');

    // Check if any control or the form group is touched/dirty
    const isTouchedOrDirty = !!(
      timeSlot.dirty ||
      timeSlot.touched ||
      openTimeControl?.dirty ||
      openTimeControl?.touched ||
      closeTimeControl?.dirty ||
      closeTimeControl?.touched
    );

    // A cross-day overlap shows at once, even on hours just loaded (it spans two
    // schedule cards, so neither slot may have been touched).
    if (this.getCrossDayOverlap(scheduleIndex, timeSlotIndex)) return true;

    if (!isTouchedOrDirty) return false;

    // Check for time range error
    const hasTimeRangeError = timeSlot.hasError('timeRangeInvalid') === true;

    // Check for required errors
    const hasOpenTimeRequired = !!openTimeControl?.hasError('required');
    const hasCloseTimeRequired = !!closeTimeControl?.hasError('required');

    // Check for duplicate time slot error
    const hasDuplicateError = timeSlot.hasError('duplicateTimeSlot') === true;

    const hasSequenceError =
      timeSlot.hasError('sequenceInvalid') === true && !!openTimeControl?.value;
    const hasCrossDayOverlap = !!this.getCrossDayOverlap(scheduleIndex, timeSlotIndex);

    return (
      hasTimeRangeError ||
      hasDuplicateError ||
      hasSequenceError ||
      hasCrossDayOverlap ||
      hasOpenTimeRequired
    );
  }

  // Get time slot error message
  getTimeSlotErrorMessage(scheduleIndex: number, timeSlotIndex: number): string {
    const timeSlot = this.getTimeSlot(scheduleIndex, timeSlotIndex);
    if (!timeSlot) return '';

    if (timeSlot.hasError('duplicateTimeSlot')) {
      return 'This time slot already exists';
    }

    const overlap = this.getCrossDayOverlap(scheduleIndex, timeSlotIndex);
    if (overlap) {
      const { day, until } = overlap;
      const time = this.convertTo12HourFormat(until);
      return `Overlaps ${day}'s hours, which run until ${time}. Open at or after ${time}.`;
    }

    if (timeSlot.hasError('sequenceInvalid')) {
      return timeSlot.getError('sequenceInvalid') === 'afterOvernight'
        ? 'A time slot that closes the next day must be the last slot of the day'
        : "Open time must be after the previous time slot's close time";
    }

    if (timeSlot.hasError('timeRangeInvalid')) {
      switch (timeSlot.getError('timeRangeInvalid') as TimeRangeError) {
        case 'same':
          return 'Close time must be different from open time';
        default:
          return `Close time must be at least ${MIN_SLOT_MINUTES} minutes after open time`;
      }
    }

    const openTimeControl = timeSlot.get('openTime');
    const closeTimeControl = timeSlot.get('closeTime');

    if (openTimeControl?.hasError('required')) {
      return 'Open time is required';
    }
    // if (closeTimeControl?.hasError('required')) {
    //   return 'Close time is required';
    // }
    return '';
  }

  // Mark all time slots as touched to show validation errors
  markAllTimeSlotsAsTouched(): void {
    this.schedules.controls.forEach((schedule, scheduleIndex) => {
      const timeSlots = this.getTimeSlots(scheduleIndex);
      timeSlots.controls.forEach((timeSlot) => {
        timeSlot.markAllAsTouched();
        timeSlot.get('openTime')?.markAsTouched();
        timeSlot.get('closeTime')?.markAsTouched();
      });
    });
  }

  // Add a new schedule
  addSchedule(): void {
    const schedule = this.fb.group({
      days: this.fb.group({
        Mon: [false],
        Tue: [false],
        Wed: [false],
        Thu: [false],
        Fri: [false],
        Sat: [false],
        Sun: [false],
      }),
      timeSlots: this.fb.array([]),
    });
    this.schedules.push(schedule);
    this.updateDayDisableStates();
    // Initially disable time slots since no days are selected
    const scheduleIndex = this.schedules.length - 1;
    this.updateTimeSlotDisabledStates(scheduleIndex);
    let tempSchedule = schedule.get('timeSlots') as FormArray;
    tempSchedule.push(this.createTimeSlot());
  }

  // Custom validator: the error value says why the range is invalid (see getTimeRangeError)
  timeRangeValidator(control: AbstractControl): ValidationErrors | null {
    const openTime = control.get('openTime')?.value;
    const closeTime = control.get('closeTime')?.value;

    if (!openTime || !closeTime) {
      return null; // Let required validator handle empty values
    }

    const rangeError = this.getTimeRangeError(openTime, closeTime);
    if (rangeError) {
      return { timeRangeInvalid: rangeError };
    }

    return null;
  }

  // Convert time string (HH:MM) to minutes
  timeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  // Create a time slot form group
  createTimeSlot(): FormGroup {
    const timeSlotGroup = this.fb.group(
      {
        openTime: ['', Validators.required],
        closeTime: ['', Validators.required],
      },
      { validators: this.timeRangeValidator.bind(this) },
    );

    return timeSlotGroup;
  }

  // Validate duplicate time slots within a schedule
  validateDuplicateTimeSlots(scheduleIndex: number): void {
    const timeSlots = this.getTimeSlots(scheduleIndex);

    // First, clear previous duplicate errors from all time slots
    // but preserve other errors
    timeSlots.controls.forEach((timeSlot) => {
      const errors = timeSlot.errors;
      if (errors && errors['duplicateTimeSlot']) {
        const newErrors: any = { ...errors };
        delete newErrors['duplicateTimeSlot'];

        if (Object.keys(newErrors).length === 0) {
          timeSlot.setErrors(null);
        } else {
          timeSlot.setErrors(newErrors);
        }
      }
    });

    // Check each time slot against all other time slots for duplicates
    for (let i = 0; i < timeSlots.length; i++) {
      const currentSlot = timeSlots.at(i) as FormGroup;
      const currentOpenTime = currentSlot.get('openTime')?.value;
      const currentCloseTime = currentSlot.get('closeTime')?.value;

      // Skip if times are not set or empty
      if (
        !currentOpenTime ||
        !currentCloseTime ||
        currentOpenTime.trim() === '' ||
        currentCloseTime.trim() === ''
      ) {
        continue;
      }

      let hasDuplicate = false;

      // Check against all other time slots (both previous and future)
      for (let j = 0; j < timeSlots.length; j++) {
        if (i === j) continue; // Skip comparing with itself

        const otherSlot = timeSlots.at(j) as FormGroup;
        const otherOpenTime = otherSlot.get('openTime')?.value;
        const otherCloseTime = otherSlot.get('closeTime')?.value;

        // Skip if other times are not set or empty
        if (
          !otherOpenTime ||
          !otherCloseTime ||
          otherOpenTime.trim() === '' ||
          otherCloseTime.trim() === ''
        ) {
          continue;
        }

        // Check if current time slot has same open and close time as other slot (exact duplicate)
        // Normalize the time strings for comparison
        const currentOpen = String(currentOpenTime).trim();
        const currentClose = String(currentCloseTime).trim();
        const otherOpen = String(otherOpenTime).trim();
        const otherClose = String(otherCloseTime).trim();

        if (currentOpen === otherOpen && currentClose === otherClose) {
          hasDuplicate = true;
          break; // Found duplicate, no need to check further
        }
      }

      // Set error if duplicate found
      if (hasDuplicate) {
        // Get current errors and preserve them
        const currentErrors = currentSlot.errors ? { ...currentSlot.errors } : {};
        currentErrors['duplicateTimeSlot'] = true;
        currentSlot.setErrors(currentErrors);

        // Mark as touched and dirty to ensure error displays
        currentSlot.markAsTouched();
        currentSlot.markAsDirty();
        currentSlot.get('openTime')?.markAsTouched();
        currentSlot.get('openTime')?.markAsDirty();
        currentSlot.get('closeTime')?.markAsTouched();
        currentSlot.get('closeTime')?.markAsDirty();

        // Force change detection to update the UI
        this.cdr.detectChanges();
      }
    }
  }

  // Validate time slots sequence order within a schedule
  validateTimeSlotsSequence(scheduleIndex: number): void {
    const timeSlots = this.getTimeSlots(scheduleIndex);
    for (let i = 1; i < timeSlots.length; i++) {
      this.validateTimeSlotSequenceAt(scheduleIndex, i);
    }
  }

  /** 2nd+ slot open time must be strictly after previous slot close time. */
  private validateTimeSlotSequenceAt(scheduleIndex: number, timeSlotIndex: number): void {
    if (timeSlotIndex < 1) {
      return;
    }

    const timeSlots = this.getTimeSlots(scheduleIndex);
    const currentSlot = timeSlots.at(timeSlotIndex) as FormGroup;

    const errors = currentSlot.errors;
    if (errors?.['sequenceInvalid']) {
      const newErrors = { ...errors };
      delete newErrors['sequenceInvalid'];
      currentSlot.setErrors(Object.keys(newErrors).length === 0 ? null : newErrors);
    }

    const currentOpenTime = currentSlot.get('openTime')?.value;
    if (!currentOpenTime || String(currentOpenTime).trim() === '') {
      return;
    }

    const previousSlot = timeSlots.at(timeSlotIndex - 1) as FormGroup;
    const previousCloseTime = previousSlot.get('closeTime')?.value;
    if (!previousCloseTime || String(previousCloseTime).trim() === '') {
      return;
    }

    const currentOpenMinutes = this.timeToMinutes(currentOpenTime);
    const previousCloseMinutes = this.timeToMinutes(previousCloseTime);
    // A slot that closes the next day must be the day's last slot.
    const previousOpenTime = previousSlot.get('openTime')?.value;
    const previousIsOvernight =
      !!previousOpenTime && this.isOvernight(previousOpenTime, previousCloseTime);

    if (previousIsOvernight || currentOpenMinutes <= previousCloseMinutes) {
      const currentErrors = currentSlot.errors ? { ...currentSlot.errors } : {};
      currentErrors['sequenceInvalid'] = previousIsOvernight ? 'afterOvernight' : true;
      currentSlot.setErrors(currentErrors);
      currentSlot.markAsTouched();
      currentSlot.markAsDirty();
      currentSlot.get('openTime')?.markAsTouched();
      currentSlot.get('openTime')?.markAsDirty();
    }
  }

  /**
   * A slot that closes the next day runs into that day's hours, so a slot on
   * the next day (any schedule card; Sun wraps to Mon) must not open before it
   * closes. Returns each overlapping next-day slot with the day it overlaps.
   * Computed from the form each time rather than stored with setErrors, which
   * other updates would wipe.
   */
  findCrossDayOverlaps(): Map<AbstractControl, { day: string; until: string }> {
    const overlaps = new Map<AbstractControl, { day: string; until: string }>();
    const schedules = (this.hoursForm?.get('schedules') as FormArray)?.controls ?? [];
    const slots: { day: string; slot: AbstractControl; open: string; close: string }[] = [];
    schedules.forEach((schedule) => {
      const days = schedule.get('days') as FormGroup;
      ((schedule.get('timeSlots') as FormArray)?.controls ?? []).forEach((slot) => {
        const open = slot.get('openTime')?.value;
        const close = slot.get('closeTime')?.value;
        if (!open || !close) return;
        this.daysOfWeek
          .filter((day) => days?.get(day)?.value === true)
          .forEach((day) => slots.push({ day, slot, open, close }));
      });
    });

    for (const spill of slots.filter((s) => this.isOvernight(s.open, s.close))) {
      const nextDay = this.daysOfWeek[(this.daysOfWeek.indexOf(spill.day) + 1) % 7];
      for (const other of slots) {
        if (
          other.day === nextDay &&
          this.timeToMinutes(other.open) < this.timeToMinutes(spill.close) &&
          !overlaps.has(other.slot)
        ) {
          overlaps.set(other.slot, { day: spill.day, until: spill.close });
        }
      }
    }
    return overlaps;
  }

  /** The overlap for one slot, or null (see findCrossDayOverlaps). */
  getCrossDayOverlap(
    scheduleIndex: number,
    timeSlotIndex: number,
  ): { day: string; until: string } | null {
    const slot = this.getTimeSlot(scheduleIndex, timeSlotIndex);
    return (slot && this.findCrossDayOverlaps().get(slot)) || null;
  }

  /** hoursForm validator: invalid (so Save is refused) while any overlap exists. */
  crossDayOverlapValidator(): ValidationErrors | null {
    return this.findCrossDayOverlaps().size > 0 ? { crossDayOverlap: true } : null;
  }

  // Check if all existing time slots have open and close times filled
  areAllTimeSlotsFilled(scheduleIndex: number): boolean {
    const timeSlots = this.getTimeSlots(scheduleIndex);
    return timeSlots.controls.every((timeSlot) => {
      const openTime = timeSlot.get('openTime')?.value;
      const closeTime = timeSlot.get('closeTime')?.value;
      return openTime && closeTime && openTime.trim() !== '' && closeTime.trim() !== '';
    });
  }

  // Add a time slot to a schedule
  addTimeSlot(scheduleIndex: number): void {
    const timeSlots = this.getTimeSlots(scheduleIndex);
    // Check if we can add more time slots (max 3) and all existing ones are filled
    if (timeSlots.length < 3 && this.areAllTimeSlotsFilled(scheduleIndex)) {
      const newTimeSlot = this.createTimeSlot();
      timeSlots.push(newTimeSlot);
      // Update disabled state based on day selection
      if (!this.hasAnyDaySelected(scheduleIndex)) {
        newTimeSlot.get('openTime')?.disable();
        newTimeSlot.get('closeTime')?.disable();
        newTimeSlot.get('openTime')?.setValue('');
        newTimeSlot.get('closeTime')?.setValue('');
      }
    }
  }

  // Check if maximum time slots reached for a schedule
  isMaxTimeSlotsReached(scheduleIndex: number): boolean {
    const timeSlots = this.getTimeSlots(scheduleIndex);
    return timeSlots.length >= 3;
  }

  // Check if add time slot button should be enabled
  canAddTimeSlot(scheduleIndex: number): boolean {
    const timeSlots = this.getTimeSlots(scheduleIndex);
    // Enable only if max slots not reached AND all existing time slots have open and close times filled
    return timeSlots.length < 3 && this.areAllTimeSlotsFilled(scheduleIndex);
  }

  // Remove a time slot from a schedule
  removeTimeSlot(scheduleIndex: number, timeSlotIndex: number): void {
    const timeSlots = this.getTimeSlots(scheduleIndex);
    if (timeSlots.length > 1) {
      timeSlots.removeAt(timeSlotIndex);
      // Revalidate for duplicates and sequence after removal
      setTimeout(() => {
        this.validateDuplicateTimeSlots(scheduleIndex);
        this.validateTimeSlotsSequence(scheduleIndex);
        this.cdr.detectChanges();
      }, 50);
    }
  }

  // Remove a schedule
  removeSchedule(scheduleIndex: number): void {
    this.schedules.removeAt(scheduleIndex);
    this.updateDayDisableStates();
  }

  // Check if a day is selected in a schedule
  isDaySelected(scheduleIndex: number, day: string): boolean {
    const daysGroup = this.schedules.at(scheduleIndex).get('days') as FormGroup;
    return daysGroup.get(day)?.value || false;
  }

  // Check if a day is selected in any other schedule
  isDaySelectedInOtherSchedule(currentScheduleIndex: number, day: string): boolean {
    for (let i = 0; i < this.schedules.length; i++) {
      if (i !== currentScheduleIndex) {
        const daysGroup = this.schedules.at(i).get('days') as FormGroup;
        if (daysGroup.get(day)?.value) {
          return true;
        }
      }
    }
    return false;
  }

  // Check if a day is disabled (selected in another schedule)
  isDayDisabled(scheduleIndex: number, day: string): boolean {
    return this.isDaySelectedInOtherSchedule(scheduleIndex, day);
  }

  // Toggle day selection
  toggleDay(scheduleIndex: number, day: string): void {
    const daysGroup = this.schedules.at(scheduleIndex).get('days') as FormGroup;
    const currentValue = daysGroup.get(day)?.value;

    // If trying to check a day that's already selected in another schedule, don't allow it
    if (!currentValue && this.isDaySelectedInOtherSchedule(scheduleIndex, day)) {
      return;
    }

    daysGroup.get(day)?.setValue(!currentValue);

    // Update disable states for all schedules
    this.updateDayDisableStates();

    // Update time slot disabled states after toggling day
    this.updateTimeSlotDisabledStates(scheduleIndex);

    // Trigger validation on all time slots when days are toggled
    this.validateTimeSlots(scheduleIndex);

    // Force change detection to update the UI
    this.cdr.detectChanges();
  }

  // Update disable states for all days across all schedules
  // Note: We rely on the template binding [disabled]="isDayDisabled()" for reactive updates
  // The template binding automatically updates when day selections change
  updateDayDisableStates(): void {
    // Template binding [disabled]="isDayDisabled()" handles updates automatically
    // No need to manually trigger change detection - Angular's change detection will pick up the changes
    // The error was caused by calling detectChanges() during construction phase
  }

  // Check if all days are selected across all schedules
  areAllDaysSelected(): boolean {
    const selectedDays = new Set<string>();

    for (let i = 0; i < this.schedules.length; i++) {
      const daysGroup = this.schedules.at(i).get('days') as FormGroup;
      for (const day of this.daysOfWeek) {
        if (daysGroup.get(day)?.value) {
          selectedDays.add(day);
        }
      }
    }

    // Check if all 7 days are selected
    return selectedDays.size === this.daysOfWeek.length;
  }

  // Check if all schedules have at least one day selected
  areAllSchedulesValid(): boolean {
    if (this.schedules.length === 0) {
      return true; // No schedules, allow adding
    }

    for (let i = 0; i < this.schedules.length; i++) {
      const daysGroup = this.schedules.at(i).get('days') as FormGroup;
      let hasSelectedDay = false;

      for (const day of this.daysOfWeek) {
        if (daysGroup.get(day)?.value) {
          hasSelectedDay = true;
          break;
        }
      }

      if (!hasSelectedDay) {
        return false; // Found a schedule with no days selected
      }
    }

    return true; // All schedules have at least one day selected
  }

  // Check if any day is selected in a schedule
  hasAnyDaySelected(scheduleIndex: number): boolean {
    const schedule = this.getSchedule(scheduleIndex);
    const days = schedule.get('days') as FormGroup;
    return this.daysOfWeek.some((day) => days.get(day)?.value === true);
  }

  // Update disabled states for time slots based on day selection
  updateTimeSlotDisabledStates(scheduleIndex: number): void {
    const hasDays = this.hasAnyDaySelected(scheduleIndex);
    const timeSlots = this.getTimeSlots(scheduleIndex);

    timeSlots.controls.forEach((timeSlot) => {
      const openTimeControl = timeSlot.get('openTime');
      const closeTimeControl = timeSlot.get('closeTime');

      if (hasDays) {
        openTimeControl?.enable();
        closeTimeControl?.enable();
      } else {
        openTimeControl?.disable();
        closeTimeControl?.disable();
        // Clear values when disabled
        openTimeControl?.setValue('');
        closeTimeControl?.setValue('');
      }
    });
  }

  // Validate all time slots in a schedule
  validateTimeSlots(scheduleIndex: number): void {
    const timeSlots = this.getTimeSlots(scheduleIndex);
    const hasDays = this.hasAnyDaySelected(scheduleIndex);

    timeSlots.controls.forEach((timeSlot) => {
      const openTimeControl = timeSlot.get('openTime');
      const closeTimeControl = timeSlot.get('closeTime');

      if (hasDays) {
        // If days are selected, mark controls as touched/dirty if they have values
        // This will trigger validation display
        if (openTimeControl?.value) {
          openTimeControl.markAsTouched();
          openTimeControl.markAsDirty();
        }
        if (closeTimeControl?.value) {
          closeTimeControl.markAsTouched();
          closeTimeControl.markAsDirty();
        }

        // Always mark the time slot form group as touched/dirty when days are checked
        // This ensures error messages are displayed even if controls don't have values yet
        timeSlot.markAsTouched();
        timeSlot.markAsDirty();

        // Trigger validation on the time slot group
        timeSlot.updateValueAndValidity();

        // Also mark controls as touched/dirty even if they don't have values
        // This ensures required validation errors are shown
        if (openTimeControl && !openTimeControl.value) {
          openTimeControl.markAsTouched();
        }
        if (closeTimeControl && !closeTimeControl.value) {
          closeTimeControl.markAsTouched();
        }
      } else {
        // If no days selected, clear validation state
        timeSlot.markAsUntouched();
        timeSlot.markAsPristine();
        openTimeControl?.markAsUntouched();
        closeTimeControl?.markAsUntouched();
      }
    });

    if (hasDays) {
      this.validateTimeSlotsSequence(scheduleIndex);
    }
  }

  // Convert time to UTC format (input is already in 24-hour format HH:MM)
  convertTimeToUTC(time: string): string {
    if (!time) return '';

    // Parse time (format: HH:MM)
    const [hours, minutes = '00'] = time.split(':');
    const hour24 = parseInt(hours, 10);
    const min = parseInt(minutes, 10);

    // Get timezone offset in minutes
    const now = new Date();
    const timezoneOffset = now.getTimezoneOffset(); // Offset in minutes

    // Create a date object for today with the local time
    const localDate = new Date();
    localDate.setHours(hour24, min, 0, 0);

    // Convert to UTC by subtracting timezone offset
    const utcTime = new Date(localDate.getTime() - timezoneOffset * 60 * 1000);
    const utcHours = utcTime.getUTCHours();
    const utcMinutes = utcTime.getUTCMinutes();

    // Return in HH:MM format
    return `${utcHours.toString().padStart(2, '0')}:${utcMinutes.toString().padStart(2, '0')}`;
  }

  // Convert UTC time back to local time in 24-hour format
  convertUTCToLocal(utcTime: string): string {
    if (!utcTime) return '08:00';

    // Parse UTC time (format: HH:MM)
    const [hours, minutes = '00'] = utcTime.split(':');
    const utcHour = parseInt(hours, 10);
    const utcMin = parseInt(minutes, 10);

    // Create a date object in UTC
    const utcDate = new Date();
    utcDate.setUTCHours(utcHour, utcMin, 0, 0);

    // Get local time
    const localHours = utcDate.getHours();
    const localMinutes = utcDate.getMinutes();

    // Return in HH:MM format (24-hour)
    return `${localHours.toString().padStart(2, '0')}:${localMinutes.toString().padStart(2, '0')}`;
  }

  // Save restaurant hours
  saveRestaurantHours(): void {
    // Mark all form controls as touched to show validation errors
    this.markAllTimeSlotsAsTouched();

    if (this.hoursForm.valid) {
      const dataToSave = this.buildHoursFormPayload();

      this.persistStoreHours(dataToSave);
    } else {
      this.toastr.error('Please fill in all required fields', 'Error');
    }
  }

  /**
   * Saves the opening hours only. Reservation (and menu) hours share the same
   * document, so they are kept as saved; Reservation settings edits those
   * (FNF-1181).
   */
  private persistStoreHours(data: ReturnType<typeof this.buildHoursFormPayload>) {
    const wasSaved = !!this.restaurantServingHoursId;
    this.ngxSpinnerService.show();
    this.restaurantService.saveServingHoursOfType('store_hours', data.serving_hours).subscribe({
      next: (doc) => {
        this.ngxSpinnerService.hide();
        this.toastr.success(
          wasSaved
            ? 'Restaurant hours updated successfully'
            : 'Restaurant hours saved successfully',
          'Success',
        );
        this.servingHours = data.serving_hours;
        this.restaurantServingHoursId = doc.serving_hour_id;
        this.currentRestaurant.serving_hours = doc;
        this.restaurantService.currentRestaurantDetails$.next(this.currentRestaurant);
        this.captureHoursFormSnapshot();
      },
      error: (err) => {
        this.ngxSpinnerService.hide();
        this.toastr.error(
          err?.error?.detail ||
            err?.error?.message ||
            'Unable to complete your action. Please try again later.',
          'Error',
        );
      },
    });
  }

  // Reset hours form
  resetHoursForm(): void {
    if (this.hasServingHours()) {
      this.populateHoursForm(this.servingHours);
    } else {
      while (this.schedules.length > 0) {
        this.schedules.removeAt(0);
      }
      this.addSchedule();
    }
    this.captureHoursFormSnapshot();
  }

  getRestaurantServingHours() {
    this.ngxSpinnerService.show();
    this.restaurantService.getRestaurantServingHours().subscribe({
      next: (response: FNFResponse<any>) => {
        this.ngxSpinnerService.hide();
        if (!response.data && response.error) {
          console.log(response.error.error?.message || response.error.message, 'Error');
          this.closedDatesState = 'failed';
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          console.log('No data found!', 'Error');
          this.closedDatesState = 'ready';
          this.ngxSpinnerService.hide();
          return;
        }
        this.dateExceptions = response.data?.date_exceptions ?? [];
        this.closedDatesState = 'ready';
        this.restaurantServingHoursId = response.data?.serving_hour_id;
        // This form edits opening hours; reservation hours live in the same
        // document and are edited in Reservation settings.
        this.servingHours = groupsOfType(response.data?.serving_hours, 'store_hours');

        // If serving hours exist, populate the form
        if (this.hasServingHours()) {
          this.populateHoursForm(this.servingHours);
        }

        this.ngxSpinnerService.hide();
      },
      error: () => {
        console.log('Unable to complete your action. Please try again later.', 'Error');
        this.closedDatesState = 'failed';
        this.ngxSpinnerService.hide();
      },
    });
  }

  // Populate hours form from API response
  populateHoursForm(servingHours: any[]): void {
    // Clear existing schedules
    while (this.schedules.length > 0) {
      this.schedules.removeAt(0);
    }

    // Create schedules from API data
    servingHours.forEach((schedule: any) => {
      const daysGroup = this.fb.group({
        Mon: [false],
        Tue: [false],
        Wed: [false],
        Thu: [false],
        Fri: [false],
        Sat: [false],
        Sun: [false],
      });

      // Set selected days
      if (schedule.days_of_the_week && Array.isArray(schedule.days_of_the_week)) {
        schedule.days_of_the_week.forEach((day: string) => {
          // Handle both "Mon" and "Mon, Tue" formats
          const dayNames = day.split(',').map((d) => d.trim());
          dayNames.forEach((dayName) => {
            if (this.daysOfWeek.includes(dayName)) {
              daysGroup.get(dayName)?.setValue(true);
            }
          });
        });
      }

      // Create time slots
      const timeSlotsArray = this.fb.array<FormGroup>([]);
      if (schedule.hours && Array.isArray(schedule.hours) && schedule.hours.length > 0) {
        schedule.hours.forEach((hour: any) => {
          const timeSlot = this.fb.group(
            {
              openTime: [hour.start_time || '', Validators.required],
              closeTime: [hour.end_time || '', Validators.required],
            },
            { validators: this.timeRangeValidator.bind(this) },
          );
          (timeSlotsArray as FormArray).push(timeSlot);
        });
      } else {
        // If no time slots, add one default empty slot
        (timeSlotsArray as FormArray).push(this.createTimeSlot());
      }

      // Create schedule form group
      const scheduleGroup = this.fb.group({
        days: daysGroup,
        timeSlots: timeSlotsArray,
      });

      this.schedules.push(scheduleGroup);
    });

    // If no schedules were created, add a default one
    if (this.schedules.length === 0) {
      this.addSchedule();
    } else {
      // Update disabled states for all schedules
      this.schedules.controls.forEach((schedule, index) => {
        this.updateTimeSlotDisabledStates(index);
        this.validateTimeSlotsSequence(index);
      });
      this.updateDayDisableStates();
      this.cdr.detectChanges();
    }

    this.captureHoursFormSnapshot();
  }
}
